import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import './PendingInvitations.css';

/**
 * Pending company invitations — a bell in the top header.
 *
 * This is the missing link that lets an OWNER actually join another company:
 * an invite creates a pending row (companies.php / users.php), and this control
 * fetches those rows (companies.php?action=invitations) and accepts them
 * (companies.php?action=accept). Once accepted, the company appears in the
 * CompanySelector so the owner can switch into it with a single login.
 *
 * Rules:
 *   • Only OWNERS can accept (their clients.id is the membership anchor), so the
 *     control is hidden for sub-users — exactly like CompanySelector.
 *   • Renders nothing when there are no pending invitations, so single-company
 *     users never see it and the header is unchanged for them.
 */
export default function PendingInvitations() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [list, setList]   = useState([]);
  const [open, setOpen]   = useState(false);
  const [busy, setBusy]   = useState(0);      // id currently being accepted
  const [err, setErr]     = useState('');
  const ref = useRef(null);

  const isOwner = user && user.actor_type !== 'user';

  const load = useCallback(() => {
    if (!isOwner) return;
    api.companies.invitations()
      .then(rows => setList(Array.isArray(rows) ? rows : []))
      .catch(()   => setList([]));
  }, [isOwner]);

  /* Load on mount and whenever the tab regains focus (a fresh invite may have
     been created while the user was away). */
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  /* Close on outside click. */
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!isOwner || list.length === 0) return null;

  const accept = async (inv) => {
    setErr('');
    setBusy(inv.id);
    try {
      await api.companies.accept({ token: inv.token });
      // Reload so CompanySelector re-fetches mine() and the new company shows up.
      // Full reload keeps every module's scope consistent, matching the switch flow.
      setTimeout(() => window.location.reload(), 50);
    } catch (e) {
      setErr(e?.message || t('pendingInvitations.acceptError'));
      setBusy(0);
    }
  };

  return (
    <div className="pending-invites" ref={ref}>
      <button
        className="pi-bell"
        type="button"
        onClick={() => setOpen(!open)}
        title={t('pendingInvitations.bellTitle', { count: list.length })}
      >
        <span className="pi-bell-icon">✉️</span>
        <span className="pi-badge">{list.length}</span>
      </button>

      {open && (
        <div className="pi-menu">
          <div className="pi-section-label">{t('pendingInvitations.companyInvitations')}</div>
          {err && <div className="pi-error">{err}</div>}
          {list.map(inv => (
            <div key={inv.id} className="pi-item">
              <div className="pi-item-body">
                <div className="pi-item-name">{inv.company_name || t('pendingInvitations.aCompany')}</div>
                <div className="pi-item-sub">{t('pendingInvitations.invitedYou')}</div>
              </div>
              <button
                className="pi-accept"
                type="button"
                disabled={busy === inv.id}
                onClick={() => accept(inv)}
              >
                {busy === inv.id ? '⏳' : t('pendingInvitations.accept')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
